import React from "react";
import { Box, Button, Divider, Link, Tooltip, Typography } from "@mui/material";
import { cardColors, cardStyle, dataRowStyle } from "../../styles/cardStyles";
import { ReactComponent as VarsomeLogo } from "../../assets/images/varsome_logo.svg";
import { ReactComponent as FranklinLogo } from "../../assets/images/franklin-dark-bg.svg";

/**
 * Component for parsing and linking variant IDs
 * @param {string} idString - String containing variant IDs separated by &
 * @return {JSX.Element} - Rendered links
 */
const VariantIDLinks = ({ idString }) => {
  if (!idString || idString === "0") {
    return (
      <Typography variant="body2" color="#111111" fontWeight="bold">
        N/A
      </Typography>
    );
  }

  // Split by & to get individual IDs
  const ids = idString.split("&").map((id) => id.trim());

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {ids.map((id, index) => {
        let url = "";
        let label = id;
        let color = "#1565c0";

        // dbSNP IDs (starting with rs)
        if (id.startsWith("rs")) {
          url = `https://www.ncbi.nlm.nih.gov/snp/${id}`;
          color = "#1565c0"; // Blue for dbSNP
        }
        // COSMIC IDs (starting with COSM, COSV, COSMxxxx, etc.)
        else if (id.startsWith("COS")) {
          url = `https://cancer.sanger.ac.uk/cosmic/search?q=${id}`;
          color = "#7b1fa2"; // Purple for COSMIC
        }
        // ClinVar IDs (starting with RCV, VCV)
        else if (id.startsWith("RCV") || id.startsWith("VCV")) {
          url = `https://www.ncbi.nlm.nih.gov/clinvar/${id}`;
          color = "#d32f2f"; // Red for ClinVar
        }
        // CM IDs (could be COSMIC legacy IDs)
        else if (id.startsWith("CM")) {
          url = `https://cancer.sanger.ac.uk/cosmic/search?q=${id}`;
          color = "#7b1fa2"; // Purple for COSMIC
          label = `${id} (COSMIC)`;
        }
        // COV IDs
        else if (id.startsWith("COV")) {
          url = `https://cancer.sanger.ac.uk/cosmic/search?q=${id}`;
          color = "#7b1fa2"; // Purple for COSMIC
        }
        // Default - no link
        else {
          return (
            <Typography
              key={index}
              variant="body2"
              color="#111111"
              fontWeight="bold"
              sx={{ fontSize: "0.8rem" }}
            >
              {id}
              {index < ids.length - 1 && ", "}
            </Typography>
          );
        }

        return (
          <React.Fragment key={index}>
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                color: color,
                fontSize: "0.8rem",
                fontWeight: "bold",
                "&:hover": {
                  color: color,
                  opacity: 0.8,
                },
              }}
            >
              {label}
            </Link>
            {index < ids.length - 1 && (
              <Typography
                variant="body2"
                color="#666666"
                sx={{ fontSize: "0.8rem" }}
              >
                ,
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

/**
 * Component for displaying a truncated value with tooltip for long text
 * @param {Object} props - Component props
 * @param {string} props.value - The value to display
 * @param {number} [props.maxLength=50] - Maximum length before truncation
 * @return {JSX.Element} - Rendered component
 */
const TruncatedValue = ({ value, maxLength = 50 }) => {
  if (!value || value === "0") {
    return (
      <Typography variant="body2" color="#111111" fontWeight="bold">
        N/A
      </Typography>
    );
  }

  const stringValue = String(value);
  const isTruncated = stringValue.length > maxLength;

  if (!isTruncated) {
    return (
      <Typography
        variant="body2"
        color="#111111"
        fontWeight="bold"
        sx={{ wordBreak: "break-word" }}
      >
        {stringValue}
      </Typography>
    );
  }

  return (
    <Tooltip title={stringValue} arrow placement="top">
      <Typography
        variant="body2"
        color="#111111"
        fontWeight="bold"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "help",
          maxWidth: "100%",
        }}
      >
        {stringValue.substring(0, maxLength)}...
      </Typography>
    </Tooltip>
  );
};

/**
 * Varsome button component
 * @param {Object} props - Component props
 * @param {Function} props.onClick - Click handler
 * @return {JSX.Element} - Rendered component
 */
function VarsomeButton({ onClick }) {
  return (
    <Button
      onClick={onClick}
      size="small"
      sx={{
        backgroundColor: "#012A56",
        "&:hover": {
          backgroundColor: "#023a74",
        },
        px: 1.5,
        py: 0.5,
      }}
      variant="contained"
    >
      <Typography
        component="div"
        color="white"
        sx={{
          whiteSpace: "nowrap",
          fontSize: 11,
          marginRight: 0.5,
        }}
      >
        View on
      </Typography>
      <VarsomeLogo width={65} />
    </Button>
  );
}

/**
 * Franklin button component
 * @param {Object} props - Component props
 * @param {Function} props.onClick - Click handler
 * @return {JSX.Element} - Rendered component
 */
function FranklinButton({ onClick }) {
  return (
    <Button
      onClick={onClick}
      size="small"
      sx={{
        backgroundColor: "#18244A",
        "&:hover": {
          backgroundColor: "#2a3a6a",
        },
        px: 1.5,
        py: 0.5,
      }}
      variant="contained"
    >
      <Typography
        component="div"
        color="white"
        sx={{
          whiteSpace: "nowrap",
          fontSize: 11,
          marginRight: 0.5,
        }}
      >
        View on
      </Typography>
      <FranklinLogo width={65} />
    </Button>
  );
}

/**
 * Component for displaying basic variant information
 * @param {Object} props - Component props
 * @param {Object} props.variant - Variant data object
 * @return {JSX.Element} - Rendered component
 */
function VariantInfoCard({ variant }) {
  if (!variant) return null;

  const chrom = variant.variant?.chrom;
  const pos = variant.variant?.pos;
  const ref = variant.variant?.ref;
  const alt = variant.variant?.alt;

  const handleVarsomeClick = () => {
    if (chrom && pos && ref && alt) {
      window.open(
        `https://varsome.com/variant/hg38/${chrom}:${pos}:${ref}:${alt}`,
        "_blank"
      );
    }
  };

  const handleFranklinClick = () => {
    if (chrom && pos && ref && alt) {
      window.open(
        `https://franklin.genoox.com/clinical-db/variant/snp/${chrom}-${pos}-${ref}-${alt}-hg38`,
        "_blank"
      );
    }
  };

  return (
    <Box sx={cardStyle(cardColors.variantInfo)}>
      <Box
        sx={{
          background: cardColors.variantInfo.header,
          p: 1,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Typography
          color={cardColors.variantInfo.headerText}
          variant="subtitle2"
          fontWeight="bold"
          align="center"
        >
          Variant Information
        </Typography>
      </Box>

      <Box sx={{ mt: 0.5 }}>
        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Chr:
          </Typography>
          <Typography variant="body2" color="#111111" fontWeight="bold">
            {variant.variant.chrom || "0"}
          </Typography>
        </Box>

        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Pos:
          </Typography>
          <Typography variant="body2" color="#111111" fontWeight="bold">
            {variant.variant.pos || "0"}
          </Typography>
        </Box>

        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Ref:
          </Typography>
          <TruncatedValue value={variant.variant.ref} maxLength={30} />
        </Box>

        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Alt:
          </Typography>
          <TruncatedValue value={variant.variant.alt} maxLength={30} />
        </Box>

        <Divider sx={{ my: 0.5 }} />

        <Box sx={{ ...dataRowStyle, alignItems: "flex-start" }}>
          <Typography
            variant="body2"
            color="#555555"
            fontWeight="medium"
            sx={{ flexShrink: 0 }}
          >
            HGVSG:
          </Typography>
          <TruncatedValue value={variant.hgvsg} maxLength={40} />
        </Box>

        <Box sx={{ ...dataRowStyle, alignItems: "flex-start" }}>
          <Typography
            variant="body2"
            color="#555555"
            fontWeight="medium"
            sx={{ flexShrink: 0 }}
          >
            HGVSC:
          </Typography>
          <TruncatedValue value={variant.hgvsc} maxLength={40} />
        </Box>

        <Box sx={{ ...dataRowStyle, alignItems: "flex-start" }}>
          <Typography
            variant="body2"
            color="#555555"
            fontWeight="medium"
            sx={{ flexShrink: 0 }}
          >
            HGVSP:
          </Typography>
          <TruncatedValue value={variant.hgvsp} maxLength={40} />
        </Box>

        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Gene:
          </Typography>
          <Typography variant="body2" color="#111111" fontWeight="bold">
            {variant.gene_symbol || "0"}
          </Typography>
        </Box>

        <Box sx={{ ...dataRowStyle, alignItems: "flex-start" }}>
          <Typography
            variant="body2"
            color="#555555"
            fontWeight="medium"
            sx={{ flexShrink: 0 }}
          >
            Consequence:
          </Typography>
          <Typography
            variant="body2"
            color="#111111"
            fontWeight="bold"
            sx={{ wordBreak: "break-word" }}
          >
            {variant.consequence || "0"}
          </Typography>
        </Box>

        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Function:
          </Typography>
          <Typography variant="body2" color="#111111" fontWeight="bold">
            {variant.function || "N/A"}
          </Typography>
        </Box>

        <Box sx={dataRowStyle}>
          <Typography variant="body2" color="#555555" fontWeight="medium">
            Impact:
          </Typography>
          <Typography variant="body2" color="#111111" fontWeight="bold">
            {variant.impact || "0"}
          </Typography>
        </Box>

        <Box sx={{ ...dataRowStyle, alignItems: "flex-start" }}>
          <Typography
            variant="body2"
            color="#555555"
            fontWeight="medium"
            sx={{ flexShrink: 0 }}
          >
            Transcript:
          </Typography>
          <TruncatedValue value={variant.feature} maxLength={35} />
        </Box>

        <Box sx={{ ...dataRowStyle, alignItems: "flex-start" }}>
          <Typography
            variant="body2"
            color="#555555"
            fontWeight="medium"
            sx={{ flexShrink: 0 }}
          >
            IDs:
          </Typography>
          <VariantIDLinks idString={variant.rs_id} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* External Links Section */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            pb: 0.5,
          }}
        >
          <VarsomeButton onClick={handleVarsomeClick} />
          <FranklinButton onClick={handleFranklinClick} />
        </Box>
      </Box>
    </Box>
  );
}

export default VariantInfoCard;
